import React, { useState, useRef, useEffect } from 'react'
import { Modal, Button, message, Slider, Spin, Tooltip } from 'antd'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc'
import { transitions as transitionTypes } from '../../helper/data.video'
import './VideoEditor.scss'

const DragHandle = SortableHandle(() => (
  <div className="video-editor__drag-handle" />
))

const SortableClip = SortableElement(
  ({ clip, displayIndex, onDelete, onClick, isActive }) => {
    return (
      <div
        className={`video-editor__clip ${
          isActive ? 'video-editor__clip--active' : ''
        }`}
        onClick={e => {
          e.stopPropagation()
          onClick(displayIndex)
        }}
      >
        <DragHandle />

        <img
          src={clip.imageSrc}
          alt={clip.shotTitle}
          className="video-editor__clip-image"
        />

        <div className="video-editor__clip-number">{displayIndex + 1}</div>

        <div className="video-editor__clip-footer">
          <span className="video-editor__clip-title">{clip.shotTitle}</span>
          <button
            onClick={e => {
              e.stopPropagation()
              onDelete(displayIndex)
            }}
            className="video-editor__clip-delete"
          >
            ×
          </button>
        </div>
      </div>
    )
  }
)

// Transition marker component between clips
const TransitionMarker = ({ transition, isEnd = false }) => {
  if (!transition) return null

  // Get the transition type and duration
  const transitionType =
    typeof transition === 'string' ? transition : transition?.transition
  const duration = typeof transition === 'object' ? transition?.duration : 1
  const label = transition?.label || 'Transition'

  // Find the matching transition from our data
  const transitionData = transitionTypes.find(t => t.id === transitionType)
  const TransitionIcon = transitionData?.icon

  const tooltipContent = (
    <div className="video-editor__transition-tooltip">
      <div className="video-editor__transition-tooltip-label">{label}</div>
      <div className="video-editor__transition-tooltip-name">
        {transitionData?.name || transitionType}
      </div>
      <div className="video-editor__transition-tooltip-duration">
        Duration: {duration}s
      </div>
    </div>
  )

  return (
    <Tooltip title={tooltipContent} placement="top">
      <div
        className={`video-editor__transition-marker ${
          isEnd ? 'video-editor__transition-marker--end' : ''
        }`}
      >
        <div className="video-editor__transition-marker-icon">
          {TransitionIcon ? <TransitionIcon size={12} /> : '⬡'}
        </div>
        <div className="video-editor__transition-marker-line" />
      </div>
    </Tooltip>
  )
}

const SortableClipList = SortableContainer(
  ({ clips, onDelete, onClick, activeIndex, timelineRef }) => (
    <div className="video-editor__timeline-clips" ref={timelineRef}>
      {clips.map((clip, index) => {
        // Separate start/shot transitions from end transitions
        const startTransitions = clip.transitions
          ? clip.transitions.filter(t => !t.isEndTransition)
          : []
        const endTransitions = clip.transitions
          ? clip.transitions.filter(t => t.isEndTransition)
          : []
        const isLastClip = index === clips.length - 1

        return (
          <React.Fragment
            key={`clip-wrapper-${clip.sceneId}-${clip.shotId}-${index}`}
          >
            {/* Show start/shot transition markers before the clip */}
            {startTransitions.length > 0 && (
              <div className="video-editor__transition-group video-editor__transition-group--start">
                {startTransitions.map((trans, tIndex) => (
                  <TransitionMarker
                    key={`trans-start-${index}-${tIndex}`}
                    transition={trans}
                  />
                ))}
              </div>
            )}
            <SortableClip
              key={`clip-${clip.sceneId}-${clip.shotId}-${index}`}
              index={index}
              displayIndex={index}
              clip={clip}
              onDelete={onDelete}
              onClick={onClick}
              isActive={activeIndex === index}
            />
            {/* Show end transition markers after the clip (only for last clip or scene end) */}
            {endTransitions.length > 0 && (
              <div className="video-editor__transition-group video-editor__transition-group--end">
                {endTransitions.map((trans, tIndex) => (
                  <TransitionMarker
                    key={`trans-end-${index}-${tIndex}`}
                    transition={trans}
                    isEnd={true}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
)

const VideoEditorModal = ({
  visible,
  scenes,
  transitions,
  onConfirm,
  onCancel,
  finalVideoExists,
  isSingleScene = false
}) => {
  const [clips, setClips] = useState([])
  const [activeClipIndex, setActiveClipIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  // Use two video elements and swap between them
  const video1Ref = useRef(null)
  const video2Ref = useRef(null)
  const [activeVideoIndex, setActiveVideoIndex] = useState(0) // 0 or 1
  const isSeekingRef = useRef(false)
  const loadingTimeoutRef = useRef(null)
  const isAutoPlayingRef = useRef(false)

  // Audio management
  const audioRef = useRef(null)
  const timelineRef = useRef(null)
  const [currentSceneId, setCurrentSceneId] = useState(null)
  const [sceneStartClipIndex, setSceneStartClipIndex] = useState(0)
  const audioSyncTimeoutRef = useRef(null)

  // Get current active video ref
  const getActiveVideoRef = () => {
    return activeVideoIndex === 0 ? video1Ref : video2Ref
  }

  // Get next (preload) video ref
  const getPreloadVideoRef = () => {
    return activeVideoIndex === 0 ? video2Ref : video1Ref
  }

  useEffect(() => {
    if (visible && scenes) {
      const allClips = []
      scenes.forEach(scene => {
        const validShots = scene.shots.filter(shot => shot.vidSrc)

        validShots.forEach((shot, shotIndex) => {
          const shotTransitionKey = `${scene.id}-${shot.id}`
          const sceneStartKey = `${scene.id}-start`
          const sceneEndKey = `${scene.id}-end`

          // Collect all transitions for this clip
          const clipTransitions = []

          // Check for scene START transition (only for first shot of the scene)
          if (shotIndex === 0 && transitions[sceneStartKey]) {
            clipTransitions.push({
              ...transitions[sceneStartKey],
              label: 'Scene Start'
            })
          }

          // Check for shot-level transition - MOVE TO END per user request
          if (transitions[shotTransitionKey]) {
            clipTransitions.push({
              ...transitions[shotTransitionKey],
              label: 'Shot Transition',
              isEndTransition: true // User requested shot transitions to be at the end/outro
            })
          }

          // Check for scene END transition (only for last shot of the scene)
          if (shotIndex === validShots.length - 1 && transitions[sceneEndKey]) {
            clipTransitions.push({
              ...transitions[sceneEndKey],
              label: 'Scene End',
              isEndTransition: true
            })
          }

          allClips.push({
            sceneId: scene.id,
            shotId: shot.id,
            sceneTitle: scene.title,
            shotTitle: shot.title,
            vidSrc: shot.vidSrc,
            imageSrc: shot.imageSrc,
            // Store all transitions for this clip
            transitions: clipTransitions.length > 0 ? clipTransitions : null,
            // Keep backward compatibility
            transition: clipTransitions[0] || null,
            audio_link: scene.audio_link
          })
        })
      })
      setClips(allClips)
      if (allClips.length > 0) {
        setActiveClipIndex(0)
      }
    }
  }, [visible, scenes, transitions])

  // Sync audio with video playback
  useEffect(() => {
    const activeVideo = getActiveVideoRef().current
    const audio = audioRef.current

    if (!activeVideo || !audio || !clips[activeClipIndex]) return

    const handleVideoPlay = () => {
      if (audio.src && !audio.ended) {
        audio.play().catch(err => console.error('Error playing audio:', err))
      }
    }

    const handleVideoPause = () => {
      if (audio.src) {
        audio.pause()
      }
    }

    const handleVideoWaiting = () => {
      if (audio.src) {
        audio.pause()
      }
    }

    const handleVideoPlaying = () => {
      if (audio.src && !audio.ended && isPlaying) {
        audio.play().catch(err => console.error('Error playing audio:', err))
      }
    }

    activeVideo.addEventListener('play', handleVideoPlay)
    activeVideo.addEventListener('pause', handleVideoPause)
    activeVideo.addEventListener('waiting', handleVideoWaiting)
    activeVideo.addEventListener('playing', handleVideoPlaying)

    return () => {
      activeVideo.removeEventListener('play', handleVideoPlay)
      activeVideo.removeEventListener('pause', handleVideoPause)
      activeVideo.removeEventListener('waiting', handleVideoWaiting)
      activeVideo.removeEventListener('playing', handleVideoPlaying)
    }
  }, [activeClipIndex, clips, isPlaying, activeVideoIndex])

  // Load audio when scene changes
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !clips[activeClipIndex]) return

    const currentClip = clips[activeClipIndex]
    const sceneId = currentClip.sceneId
    const audioLink = currentClip.audio_link

    // Check if we've entered a new scene
    if (sceneId !== currentSceneId) {
      setCurrentSceneId(sceneId)

      // Find the first clip of this scene
      const sceneStart = clips.findIndex(clip => clip.sceneId === sceneId)
      setSceneStartClipIndex(sceneStart)

      // Load audio if scene has audio_link
      if (audioLink) {
        audio.src = audioLink
        audio.load()

        // Calculate elapsed time from start of scene
        let elapsedTime = 0
        for (let i = sceneStart; i < activeClipIndex; i++) {
          const video = document.createElement('video')
          video.src = clips[i].vidSrc
          // This is approximate - ideally we'd have duration stored with each clip
        }

        audio.currentTime = 0 // Start from beginning of audio for now

        if (isPlaying) {
          audio.play().catch(err => console.error('Error playing audio:', err))
        }
      } else {
        // Stop and clear audio if no audio_link
        audio.pause()
        audio.src = ''
      }
    } else if (audioLink && audio.src !== audioLink) {
      // Same scene but audio wasn't loaded (shouldn't normally happen)
      audio.src = audioLink
      audio.load()
      audio.currentTime = 0
    }

    // Check if we've left the scene
    const isStillInScene = currentClip.sceneId === sceneId
    if (!isStillInScene && audio.src) {
      audio.pause()
      audio.src = ''
    }
  }, [activeClipIndex, clips, currentSceneId, isPlaying])

  useEffect(() => {
    const activeVideo = getActiveVideoRef().current
    const preloadVideo = getPreloadVideoRef().current

    if (!activeVideo || !clips[activeClipIndex]) return

    // Clear any existing loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
    }

    // Only show loading after a delay (300ms) - this prevents flicker on quick loads
    // Skip loading indicator during autoplay transitions since video is preloaded
    if (!isAutoPlayingRef.current) {
      loadingTimeoutRef.current = setTimeout(() => {
        setIsLoading(true)
      }, 300)
    }

    // Load current clip in active video
    if (activeVideo.src !== clips[activeClipIndex].vidSrc) {
      activeVideo.src = clips[activeClipIndex].vidSrc
      activeVideo.load()
    } else {
      // Video already loaded, clear loading immediately
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      setIsLoading(false)
    }

    // Preload next clip in the other video element
    if (preloadVideo && activeClipIndex < clips.length - 1) {
      const nextClip = clips[activeClipIndex + 1]
      if (nextClip && preloadVideo.src !== nextClip.vidSrc) {
        preloadVideo.src = nextClip.vidSrc
        preloadVideo.load()
      }
    }

    const handleLoadedMetadata = () => {
      setDuration(activeVideo.duration || 0)
      setCurrentTime(0)

      // Clear loading timeout and hide loader
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      setIsLoading(false)

      if (isPlaying) {
        const playPromise = activeVideo.play()
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            console.error('Error playing video:', err)
            setIsPlaying(false)
          })
        }
      }
    }

    const handleTimeUpdate = () => {
      if (!isSeekingRef.current) {
        setCurrentTime(activeVideo.currentTime || 0)
      }
    }

    const handleEnded = () => {
      const audio = audioRef.current

      // Auto-advance to next clip if playing
      if (isPlaying && activeClipIndex < clips.length - 1) {
        const nextClip = clips[activeClipIndex + 1]
        const currentClip = clips[activeClipIndex]

        // Check if next clip is in the same scene
        const isSameScene = nextClip.sceneId === currentClip.sceneId

        if (!isSameScene && audio) {
          // Leaving the scene, stop audio
          audio.pause()
        }

        // Mark as autoplay transition
        isAutoPlayingRef.current = true
        // Swap to preloaded video for instant playback
        setActiveVideoIndex(prev => (prev === 0 ? 1 : 0))
        setActiveClipIndex(prev => prev + 1)
        // Reset autoplay flag after transition
        setTimeout(() => {
          isAutoPlayingRef.current = false
        }, 100)
      } else {
        setIsPlaying(false)
        if (activeClipIndex === clips.length - 1) {
          setActiveClipIndex(0)
          // Stop audio when reaching the end
          if (audio) {
            audio.pause()
            audio.currentTime = 0
          }
        }
      }
    }

    const handleSeeking = () => {
      isSeekingRef.current = true
    }

    const handleSeeked = () => {
      isSeekingRef.current = false
    }

    const handleCanPlay = () => {
      // Clear loading timeout and hide loader when video can play
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      setIsLoading(false)
    }

    const handleWaiting = () => {
      // Only show loading if we're actually waiting (buffering)
      if (!isAutoPlayingRef.current) {
        loadingTimeoutRef.current = setTimeout(() => {
          setIsLoading(true)
        }, 300)
      }
    }

    activeVideo.addEventListener('loadedmetadata', handleLoadedMetadata)
    activeVideo.addEventListener('timeupdate', handleTimeUpdate)
    activeVideo.addEventListener('ended', handleEnded)
    activeVideo.addEventListener('seeking', handleSeeking)
    activeVideo.addEventListener('seeked', handleSeeked)
    activeVideo.addEventListener('canplay', handleCanPlay)
    activeVideo.addEventListener('waiting', handleWaiting)

    return () => {
      // Clear timeout on cleanup
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current)
      }
      activeVideo.removeEventListener('loadedmetadata', handleLoadedMetadata)
      activeVideo.removeEventListener('timeupdate', handleTimeUpdate)
      activeVideo.removeEventListener('ended', handleEnded)
      activeVideo.removeEventListener('seeking', handleSeeking)
      activeVideo.removeEventListener('seeked', handleSeeked)
      activeVideo.removeEventListener('canplay', handleCanPlay)
      activeVideo.removeEventListener('waiting', handleWaiting)
    }
  }, [activeClipIndex, clips, isPlaying, activeVideoIndex])

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return

    const newClips = [...clips]
    const [removed] = newClips.splice(oldIndex, 1)
    newClips.splice(newIndex, 0, removed)
    setClips(newClips)

    if (activeClipIndex === oldIndex) {
      setActiveClipIndex(newIndex)
    } else if (oldIndex < activeClipIndex && newIndex >= activeClipIndex) {
      setActiveClipIndex(prev => prev - 1)
    } else if (oldIndex > activeClipIndex && newIndex <= activeClipIndex) {
      setActiveClipIndex(prev => prev + 1)
    }

    message.success('Clip reordered')
  }

  // Auto-scroll timeline when active clip changes
  useEffect(() => {
    if (timelineRef.current && activeClipIndex !== undefined) {
      const activeElement = timelineRef.current.children[activeClipIndex]
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
    }
  }, [activeClipIndex])

  const handleDelete = index => {
    Modal.confirm({
      title: 'Delete clip',
      content: 'Remove this clip from the final video?',
      centered: true,
      okText: 'Delete',
      okType: 'danger',
      onOk() {
        const newClips = clips.filter((_, i) => i !== index)
        setClips(newClips)

        if (newClips.length === 0) {
          setActiveClipIndex(0)
          setIsPlaying(false)
        } else if (activeClipIndex >= newClips.length) {
          setActiveClipIndex(newClips.length - 1)
        } else if (activeClipIndex === index && isPlaying) {
          setIsPlaying(false)
        }

        message.success('Clip deleted')
      }
    })
  }

  const handlePlayPause = () => {
    const video = getActiveVideoRef().current
    const audio = audioRef.current
    if (!video) return

    if (isPlaying) {
      video.pause()
      if (audio && audio.src) {
        audio.pause()
      }
      setIsPlaying(false)
    } else {
      const playPromise = video.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true)
            if (audio && audio.src && !audio.ended) {
              audio
                .play()
                .catch(err => console.error('Error playing audio:', err))
            }
          })
          .catch(err => {
            console.error('Error playing video:', err)
            message.error('Failed to play video')
          })
      }
    }
  }

  const handlePrevious = () => {
    if (activeClipIndex > 0) {
      const audio = audioRef.current
      setActiveClipIndex(prev => prev - 1)
      setIsPlaying(false)

      // Pause audio when manually changing clips
      if (audio && audio.src) {
        audio.pause()
      }
    }
  }

  const handleNext = () => {
    if (activeClipIndex < clips.length - 1) {
      const audio = audioRef.current

      // If next video is preloaded, swap to it for instant playback
      const preloadVideo = getPreloadVideoRef().current
      if (
        preloadVideo &&
        preloadVideo.src === clips[activeClipIndex + 1].vidSrc
      ) {
        setActiveVideoIndex(prev => (prev === 0 ? 1 : 0))
      }
      setActiveClipIndex(prev => prev + 1)
      setIsPlaying(false)

      // Pause audio when manually changing clips
      if (audio && audio.src) {
        audio.pause()
      }
    }
  }

  const handleSeek = value => {
    const video = getActiveVideoRef().current
    if (video && !isNaN(value)) {
      video.currentTime = value
      setCurrentTime(value)
    }
  }

  const handleClipClick = index => {
    const audio = audioRef.current
    // Stop playing when switching clips manually
    setIsPlaying(false)
    // Pause audio
    if (audio && audio.src) {
      audio.pause()
    }
    // Switch active clip
    setActiveClipIndex(index)
  }

  const handleConfirm = () => {
    if (clips.length === 0) {
      message.error('You need at least one clip to generate the final video')
      return
    }

    if (finalVideoExists) {
      Modal.confirm({
        title: 'Overwrite Final Video',
        content:
          'A final video already exists. Generating a new one will overwrite the existing video. Do you want to continue?',
        centered: true,
        okText: 'Yes, Overwrite',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          onConfirm(clips)
        }
      })
    } else {
      Modal.confirm({
        title: 'Generate Final Video',
        content: 'Are you sure you want to generate the final video?',
        centered: true,
        okText: 'Yes, Generate',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk() {
          onConfirm(clips)
        }
      })
    }
  }

  const formatTime = seconds => {
    if (isNaN(seconds) || !isFinite(seconds)) {
      return '00:00'
    }
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  return (
    <Modal
      visible={visible}
      title={null}
      width="95%"
      style={{ top: 20, maxWidth: '1400px' }}
      centered
      onCancel={onCancel}
      footer={null}
      bodyStyle={{ padding: 0, background: '#141414' }}
      destroyOnClose
      getContainer={false}
      closable={false}
    >
      <div className="video-editor">
        {/* Header */}
        <div className="video-editor__header">
          <div>
            <h3 className="video-editor__title">Video Preview</h3>
            <div className="video-editor__subtitle">
              {clips.length} clip{clips.length !== 1 ? 's' : ''} in timeline
            </div>
          </div>
          <div className="video-editor__header-actions">
            <Button onClick={onCancel}>Cancel</Button>
            {!isSingleScene && (
              <Button
                type="primary"
                icon="play-circle"
                onClick={handleConfirm}
                disabled={clips.length === 0}
              >
                Generate Final Video
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="video-editor__content">
          {/* Video Preview */}
          <div className="video-editor__preview">
            {clips.length > 0 ? (
              <>
                {/* Loading Spinner */}
                {isLoading && (
                  <div className="video-editor__loading">
                    <Spin size="large" />
                  </div>
                )}

                {/* Video 1 */}
                <video
                  ref={video1Ref}
                  className="video-editor__video"
                  style={{ display: activeVideoIndex === 0 ? 'block' : 'none' }}
                  preload="auto"
                />

                {/* Video 2 - for seamless switching */}
                <video
                  ref={video2Ref}
                  className="video-editor__video"
                  style={{ display: activeVideoIndex === 1 ? 'block' : 'none' }}
                  preload="auto"
                />

                {/* Hidden audio element for scene audio */}
                <audio ref={audioRef} preload="auto" />

                {/* Clip Info Overlay */}
                <div className="video-editor__clip-info">
                  <div className="video-editor__clip-info-title">
                    Clip {activeClipIndex + 1} of {clips.length}
                  </div>
                  <div className="video-editor__clip-info-subtitle">
                    {clips[activeClipIndex]?.sceneTitle} -{' '}
                    {clips[activeClipIndex]?.shotTitle}
                  </div>
                </div>

                {/* Playing Indicator */}
                {isPlaying && (
                  <div className="video-editor__playing-indicator">
                    <span>▶</span> Playing
                  </div>
                )}
              </>
            ) : (
              <div className="video-editor__empty">
                <div className="video-editor__empty-icon">📹</div>
                <div>No clips available</div>
                <div className="video-editor__empty-subtitle">
                  Generate videos first
                </div>
              </div>
            )}
          </div>

          {/* Video Controls */}
          {clips.length > 0 && (
            <div className="video-editor__controls">
              {/* Timeline Slider */}
              <div className="video-editor__slider">
                <Slider
                  min={0}
                  max={duration || 100}
                  value={currentTime}
                  onChange={handleSeek}
                  step={0.1}
                  tooltipVisible={false}
                  trackStyle={{ background: '#1890ff' }}
                  handleStyle={{ borderColor: '#1890ff' }}
                />
              </div>

              {/* Playback Controls */}
              <div className="video-editor__playback">
                <div className="video-editor__playback-buttons">
                  <Button
                    icon="step-backward"
                    onClick={handlePrevious}
                    disabled={activeClipIndex === 0}
                    className="video-editor__control-btn"
                  />
                  <Button
                    icon={isPlaying ? 'pause' : 'caret-right'}
                    onClick={handlePlayPause}
                    type="primary"
                    className="video-editor__control-btn video-editor__control-btn--play"
                    style={{ width: '48px' }}
                  />
                  <Button
                    icon="step-forward"
                    onClick={handleNext}
                    disabled={activeClipIndex === clips.length - 1}
                    className="video-editor__control-btn"
                  />

                  <span className="video-editor__time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="video-editor__timeline">
            <div className="video-editor__timeline-header">
              <span className="video-editor__timeline-title">Timeline</span>
              <span className="video-editor__timeline-help">
                Drag clips to reorder • Click to select • Click × to delete
              </span>
            </div>

            <SortableClipList
              clips={clips}
              onSortEnd={handleSortEnd}
              onDelete={handleDelete}
              onClick={handleClipClick}
              activeIndex={activeClipIndex}
              timelineRef={timelineRef}
              axis="x"
              lockAxis="x"
              useDragHandle
              helperClass="sortable-helper"
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default VideoEditorModal
